import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderKanban, AlertTriangle, Zap, ListChecks,
} from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import EmptyState from '../EmptyState';
import KpiTile from './KpiTile';
import RangoToggle from './RangoToggle';
import ResumenEjecutivoCard from './ResumenEjecutivoCard';
import ProyectoHealthCard from './ProyectoHealthCard';
import AlertasFeed from './AlertasFeed';

const RANGO_STORAGE_KEY = 'metricas-rango';

/**
 * Vista de Metricas del modulo PM (tab "Metricas" como primera).
 * Layout:
 *   1. Toolbar: titulo + RangoToggle
 *   2. Header KPIs (4 tiles)
 *   3. Resumen ejecutivo IA (card ancha)
 *   4. Grid de proyectos con health + IA insight
 *   5. Alertas inteligentes (lista)
 */
const MetricasView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rango, setRango] = useState(() => {
    if (typeof window === 'undefined') return 'semana';
    return window.localStorage.getItem(RANGO_STORAGE_KEY) || 'semana';
  });

  // Estado de regeneracion IA (por tipo)
  const [generating, setGenerating] = useState({ resumen_semanal: false, alertas: false, health_insights: false });
  const [iaError, setIaError] = useState({ resumen_semanal: null, alertas: null });

  useEffect(() => {
    try { window.localStorage.setItem(RANGO_STORAGE_KEY, rango); } catch (_) {}
  }, [rango]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axiosInstance.get(`/api/proyectos/metricas?rango=${rango}`);
      setData(resp);
    } catch (err) {
      console.error('Error cargando metricas:', err);
    } finally {
      setLoading(false);
    }
  }, [rango]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-dispatch: una sola vez por sesion + rango. Si el GET trajo todos los
  // caches IA vigentes, no dispara nada. Si alguno viene null, regenera UNA
  // sola tanda con ?tipo=all para no triplicar latencia.
  const [autoDispatched, setAutoDispatched] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rango invalida la guardia
  useEffect(() => { setAutoDispatched(false); }, [rango]);

  // Auto-dispatch tras el primer fetch del rango si falta algun cache IA.
  useEffect(() => {
    if (!data || loading || autoDispatched) return;
    const faltaResumen = data.resumen_ia === null;
    const faltaAlertas = data.alertas_ia === null;
    const proyectos = data.proyectos || [];
    const faltaInsights = proyectos.length > 0 && !proyectos.some((p) => p.ia_insight);
    if (!faltaResumen && !faltaAlertas && !faltaInsights) return;

    setAutoDispatched(true);
    // Pintar skeletons inmediatamente de los tipos que se van a generar
    setGenerating({
      resumen_semanal: faltaResumen,
      alertas: faltaAlertas,
      health_insights: faltaInsights,
    });
    setIaError({ resumen_semanal: null, alertas: null });

    (async () => {
      try {
        await axiosInstance.post(`/api/proyectos/metricas/regenerar-ia?tipo=all&rango=${rango}`);
        await fetchData();
      } catch (err) {
        console.error('Error auto-regenerando IA:', err);
        setIaError({
          resumen_semanal: faltaResumen ? 'Error generando' : null,
          alertas: faltaAlertas ? 'Error generando' : null,
        });
      } finally {
        setGenerating({ resumen_semanal: false, alertas: false, health_insights: false });
      }
    })();
  }, [data, loading, autoDispatched, rango, fetchData]);

  const regenerar = useCallback(async (tipo) => {
    setGenerating((s) => ({ ...s, [tipo]: true }));
    setIaError((e) => ({ ...e, [tipo]: null }));
    try {
      await axiosInstance.post(`/api/proyectos/metricas/regenerar-ia?tipo=${tipo}&rango=${rango}`);
      await fetchData();
    } catch (err) {
      console.error(`Error regenerando IA (${tipo}):`, err);
      setIaError((e) => ({ ...e, [tipo]: err.response?.data?.error || 'Error generando' }));
    } finally {
      setGenerating((s) => ({ ...s, [tipo]: false }));
    }
  }, [rango, fetchData]);

  const kpis = data?.kpis;
  const proyectos = data?.proyectos || [];
  const totalProyectos = proyectos.length;

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA]">
      {/* Toolbar sticky */}
      <div className="sticky top-0 z-10 bg-[#FAFAFA]/95 backdrop-blur-sm border-b border-slate-200/60">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-slate-900 tracking-tight">
              Métricas
            </h2>
            <span className="text-xs text-slate-400 tabular-nums">
              {totalProyectos} {totalProyectos === 1 ? 'proyecto' : 'proyectos'}
            </span>
          </div>
          <RangoToggle value={rango} onChange={setRango} />
        </div>
      </div>

      <div className="px-6 py-5 space-y-5 max-w-[1400px] mx-auto">
        {/* KPIs */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile
              label="Proyectos activos"
              value={loading ? '—' : kpis?.proyectos_activos ?? 0}
              sub={!loading && kpis?.proyectos_en_riesgo > 0
                ? `${kpis.proyectos_en_riesgo} en riesgo`
                : !loading ? 'Todos a tiempo' : null}
              icon={FolderKanban}
              accent="slate"
              loading={loading}
            />
            <KpiTile
              label="Tareas completadas"
              value={loading ? '—' : kpis?.tareas_completadas_periodo ?? 0}
              sub="en este período"
              delta={!loading && kpis
                ? (kpis.tareas_completadas_periodo - kpis.tareas_completadas_periodo_anterior)
                : null}
              deltaSuffix="vs anterior"
              icon={Zap}
              accent="cyan"
              loading={loading}
            />
            <KpiTile
              label="Tu carga"
              value={loading ? '—' : kpis?.mis_tareas_activas ?? 0}
              sub={!loading && kpis?.mis_tareas_vencen_periodo > 0
                ? `${kpis.mis_tareas_vencen_periodo} vencen en período`
                : null}
              icon={ListChecks}
              accent="cyan"
              loading={loading}
            />
            <KpiTile
              label="Atrasadas"
              value={loading ? '—' : kpis?.atrasadas_totales ?? 0}
              sub="tareas vencidas tuyas"
              icon={AlertTriangle}
              accent={kpis?.atrasadas_totales > 0 ? 'rose' : 'slate'}
              loading={loading}
            />
          </div>
        </section>

        {/* Resumen ejecutivo IA */}
        <section>
          <ResumenEjecutivoCard
            resumen={data?.resumen_ia}
            generating={generating.resumen_semanal}
            error={iaError.resumen_semanal}
            onRegenerar={() => regenerar('resumen_semanal')}
          />
        </section>

        {/* Grid de proyectos */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-slate-600">
              Proyectos
            </h3>
          </div>

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200/70 p-4 skeleton">
                  <div className="h-3 w-2/3 bg-slate-100 rounded mb-3" />
                  <div className="h-2 w-12 bg-slate-100 rounded mb-4" />
                  <div className="h-1.5 w-full bg-slate-100 rounded mb-3" />
                  <div className="h-2.5 w-3/4 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          )}

          {!loading && proyectos.length === 0 && (
            <EmptyState
              icon={FolderKanban}
              title="Sin proyectos para mostrar"
              description="Cuando te unas a un proyecto va a aparecer acá con sus métricas."
              variant="centered"
              className="py-16"
            />
          )}

          {!loading && proyectos.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {proyectos.map((p) => (
                <ProyectoHealthCard
                  key={p.id}
                  proyecto={p}
                  generandoInsight={generating.health_insights && !p.ia_insight}
                />
              ))}
            </div>
          )}
        </section>

        {/* Alertas */}
        <section>
          <AlertasFeed
            alertas={data?.alertas_ia}
            generating={generating.alertas}
            error={iaError.alertas}
            onRegenerar={() => regenerar('alertas')}
          />
        </section>
      </div>
    </div>
  );
};

export default MetricasView;
